import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import { auth, db, storage } from './firebase-config.js';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import {
  Timestamp,
  arrayUnion,
  collection,
  doc as firestoreDoc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc
} from 'firebase/firestore';
import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';
import { MAX_ANEXOS, MAX_PROTOCOLO_ATTEMPTS } from './src/constants.js';
import { generatePin, generateProtocolo, validateAnexo } from './src/utils.js';
import { buscarDenunciaPorPin, enviarMensagemRelator } from './src/services/denuncias.js';
import { AdminLoginView, AdminPanel, Navbar, PublicForm, SuccessView, TrackView } from './src/components/index.js';

// --- Componente Principal ---

function App() {
  const [view, setView] = useState('public');
  const [denuncias, setDenuncias] = useState([]);
  const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);
  const [adminEmailInput, setAdminEmailInput] = useState('');
  const [adminPasswordInput, setAdminPasswordInput] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loggedAdminEmail, setLoggedAdminEmail] = useState('');
  const [adminSearch, setAdminSearch] = useState('');
  const [adminActiveChatId, setAdminActiveChatId] = useState(null);
  const [adminNovaMensagem, setAdminNovaMensagem] = useState('');
  const [categoria, setCategoria] = useState('');
  const [descricao, setDescricao] = useState('');
  const [anonimo, setAnonimo] = useState(false);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [anexos, setAnexos] = useState([]);
  const [protocoloGerado, setProtocoloGerado] = useState('');
  const [pinGerado, setPinGerado] = useState('');
  const [buscaProtocolo, setBuscaProtocolo] = useState('');
  const [buscaPin, setBuscaPin] = useState('');
  
  // AQUI: Usando trackedId como "chave" para iniciar o listener
  const [trackedId, setTrackedId] = useState(null);
  const [denunciaRastreada, setDenunciaRastreada] = useState(null);
  
  const [novaMensagem, setNovaMensagem] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    let unsubscribeFirestore = null;

    const unsubscribeAuth = onAuthStateChanged(auth, user => {
      setIsAdminLoggedIn(!!user);
      if (user) {
        setLoggedAdminEmail(user.email);
        const denunciasQuery = query(collection(db, "denuncias"), orderBy("dataCompleta", "desc"));
        unsubscribeFirestore = onSnapshot(
            denunciasQuery,
            snapshot => {
              const denunciasData = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
              setDenuncias(denunciasData);
            },
            error => console.error("Erro no listener de denúncias:", error)
          );
      } else {
        if (unsubscribeFirestore) {
          unsubscribeFirestore();
          unsubscribeFirestore = null;
        }
        setDenuncias([]);
        setLoggedAdminEmail('');
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeFirestore) unsubscribeFirestore();
    };
  }, []);

  useEffect(() => {
    if (!trackedId) setDenunciaRastreada(null);
  }, [trackedId]);

  const handleFileChange = useCallback((e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const invalidMessage = selectedFiles.map(validateAnexo).find(Boolean);
    if (invalidMessage) {
      alert(invalidMessage);
      e.target.value = '';
      return;
    }

    setAnexos(prev => {
      if (prev.length + selectedFiles.length > MAX_ANEXOS) {
        alert(`É permitido anexar no máximo ${MAX_ANEXOS} arquivos por relato.`);
        return prev;
      }
      return [...prev, ...selectedFiles];
    });
    e.target.value = '';
  }, []);

  const removeAnexo = useCallback((index) => {
    setAnexos(prev => prev.filter((_, i) => i !== index));
  }, []);

  const gerarProtocoloDisponivel = useCallback(async () => {
    for (let attempt = 0; attempt < MAX_PROTOCOLO_ATTEMPTS; attempt += 1) {
      const protocolo = generateProtocolo();
      const existingDoc = await getDoc(firestoreDoc(db, "denuncias", protocolo));
      if (!existingDoc.exists) return protocolo;
    }
    throw new Error("Não foi possível gerar um protocolo único. Tente novamente.");
  }, []);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!categoria || !descricao) return alert("Por favor, preencha a categoria e a descrição.");
    if (!anonimo && (!nome || (!email && !telefone))) return alert("Para esta categoria ou escolha, nome e pelo menos um contato (email ou telefone) são obrigatórios.");
    const invalidAnexoMessage = anexos.map(validateAnexo).find(Boolean);
    if (invalidAnexoMessage) return alert(invalidAnexoMessage);
    
    setIsSubmitting(true);
    try {
        const novoProtocolo = await gerarProtocoloDisponivel();
        const novoPin = generatePin();
        const dataHoraAtual = Timestamp.now();

        const arquivosProcessados = await Promise.all(
          anexos.map(async (file) => {
            const anexoRef = storageRef(storage, `anexos/${novoProtocolo}/${file.name}`);
            await uploadBytes(anexoRef, file);
            const url = await getDownloadURL(anexoRef);
            return { nomeExibicao: file.name, url, nomeOriginal: file.name };
          })
        );

        const novaDenuncia = {
          id: novoProtocolo, pin: novoPin, dataCompleta: dataHoraAtual, categoria, descricao, anonimo, 
          nome: anonimo ? 'Anônimo' : nome, email: anonimo ? 'N/A' : email, telefone: anonimo ? 'N/A' : telefone, 
          arquivos: arquivosProcessados, status: 'Em Aberto', setor: 'Não Atribuído',
          mensagens: [{
            id: Date.now(), remetenteExibicao: anonimo ? 'Anônimo (Autor)' : nome,
            remetenteInterno: anonimo ? 'Anônimo' : email || telefone, texto: descricao, data: new Date().toLocaleString('pt-BR')
          }]
        };
        await setDoc(firestoreDoc(db, "denuncias", novoProtocolo), novaDenuncia);

        setProtocoloGerado(novoProtocolo);
        setPinGerado(novoPin);
        setView('success');
        setCategoria(''); setDescricao(''); setAnonimo(false); setNome(''); setEmail(''); setTelefone(''); setAnexos([]);
    } catch (error) {
        console.error("Erro ao enviar denúncia:", error);
        alert(`Ocorreu um erro ao enviar a denúncia. Por favor, verifique sua conexão ou as permissões de armazenamento. Erro: ${error.message}`);
    } finally {
        setIsSubmitting(false);
    }
  }, [categoria, descricao, anonimo, nome, email, telefone, anexos, isSubmitting, gerarProtocoloDisponivel]);

  const updateDenuncia = useCallback(async (id, campo, valor) => {
    try {
      await updateDoc(firestoreDoc(db, "denuncias", id), { [campo]: valor });
    } catch (error) {
      console.error("Erro ao atualizar denúncia:", error);
      alert("Não foi possível atualizar a denúncia. Tente novamente.");
    }
  }, []);

  const handleBuscarDenuncia = useCallback(async (e) => {
    e.preventDefault();
    try {
        const protocolo = buscaProtocolo.trim().toUpperCase();
        const result = await buscarDenunciaPorPin({ protocolo, pin: buscaPin.trim() });
        const denuncia = result.data.denuncia;
        setDenunciaRastreada(denuncia);
        setTrackedId(denuncia.id);
    } catch (error) {
        console.error("Erro ao buscar denúncia:", error);
        alert(error.message || "Protocolo ou PIN incorretos.");
    }
  }, [buscaProtocolo, buscaPin]);

  const enviarMensagemUsuarioChat = useCallback(async (e) => {
    e.preventDefault();
    if (!novaMensagem.trim() || !denunciaRastreada) return;
    try {
      const result = await enviarMensagemRelator({
        protocolo: denunciaRastreada.id,
        pin: buscaPin.trim(),
        texto: novaMensagem,
      });
      setDenunciaRastreada(result.data.denuncia);
      setNovaMensagem('');
    } catch (error) {
      console.error("Erro ao enviar mensagem do relator:", error);
      alert(error.message || "Não foi possível enviar a mensagem. Tente novamente.");
    }
  }, [novaMensagem, denunciaRastreada, buscaPin]);
  
  const handleAdminLogin = useCallback(async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, adminEmailInput, adminPasswordInput);
      setLoginError(''); setAdminEmailInput(''); setAdminPasswordInput('');
    } catch (error) {
      setLoginError('E-mail ou senha incorretos.');
    }
  }, [adminEmailInput, adminPasswordInput]);

  const handleAdminLogout = useCallback(async () => {
    try {
      await signOut(auth);
      setAdminActiveChatId(null);
    } catch (error) {
      console.error("Erro ao sair do painel:", error);
      alert("Não foi possível sair do painel. Tente novamente.");
    }
  }, []);

  const enviarMensagemAdminChat = useCallback(async (e, denuncia) => {
    e.preventDefault();
    if (!adminNovaMensagem.trim() || !denuncia.id) return;
    try {
      const msg = { id: Date.now(), remetenteExibicao: 'RTA responde', remetenteInterno: loggedAdminEmail, texto: adminNovaMensagem, data: new Date().toLocaleString('pt-BR') };
      await updateDoc(firestoreDoc(db, "denuncias", denuncia.id), { mensagens: arrayUnion(msg) });
      setAdminNovaMensagem('');
    } catch (error) {
      console.error("Erro ao enviar mensagem do admin:", error);
      alert("Não foi possível enviar a resposta. Tente novamente.");
    }
  }, [adminNovaMensagem, loggedAdminEmail]);

  const props = {
      view, setView, denuncias, isAdminLoggedIn, adminEmailInput, setAdminEmailInput, adminPasswordInput, setAdminPasswordInput,
      loginError, loggedAdminEmail, adminSearch, setAdminSearch, adminActiveChatId, setAdminActiveChatId, adminNovaMensagem,
      setAdminNovaMensagem, categoria, setCategoria, descricao, setDescricao, anonimo, setAnonimo, nome, setNome, email,
      setEmail, telefone, setTelefone, anexos, setAnexos, protocoloGerado, pinGerado, buscaProtocolo, setBuscaProtocolo,
      buscaPin, setBuscaPin, denunciaRastreada, setDenunciaRastreada, novaMensagem, setNovaMensagem, isSubmitting,
      handleFileChange, removeAnexo, handleSubmit, updateDenuncia, handleBuscarDenuncia, enviarMensagemUsuarioChat,
      handleAdminLogin, handleAdminLogout, enviarMensagemAdminChat, trackedId, setTrackedId
  };

  const publicFormProps = {
    states: { categoria, descricao, anonimo, nome, email, telefone, anexos, isSubmitting },
    handlers: { setCategoria, setDescricao, setAnonimo, setNome, setEmail, setTelefone, handleFileChange, removeAnexo, handleSubmit }
  };

  const renderContent = () => {
    switch (view) {
      case 'public':
        return <PublicForm {...publicFormProps} />;
      case 'success':
        return <SuccessView {...{ protocoloGerado, pinGerado, setBuscaProtocolo, setBuscaPin, setView, setTrackedId }} />;
      case 'track':
        return <TrackView {...props} />;
      case 'admin':
        return isAdminLoggedIn ? <AdminPanel {...props} /> : <AdminLoginView {...props} />;
      default:
        return <PublicForm {...publicFormProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-12 font-sans">
      <Navbar view={view} setView={setView} setTrackedId={setTrackedId} />
      <main className="transition-all duration-300">
        {renderContent()}
      </main>
    </div>
  );
}

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(<App />);
